<template>
  <div
    v-if="!loading"
    class="row"
  >
    <h2 style="margin-left: 15px;">
      <router-link
        :to="{ name: 'coach-dashboard' }"
      >
        Dashboard
      </router-link>
      <span v-if="assignment.assignmentId">
        |
        <router-link
          :to="{ name: 'preview-assignment',
                 params: {assignmentId: assignment.assignmentId } }"
        >
          {{ assignment.name }}
        </router-link>
      </span>
      <span v-else>
        |
        Add Assignment
      </span>
      <span v-if="assignment.assignmentId && stepNum !== assignmentStepsByName.assignment">
        |
        Edit {{ assignment.isTemplate ? '' : 'Assignment' }}
      </span>
    </h2>

    <!-- STEP 1: Name/Description -->
    <assignment-edit-description
      v-if="(!stepNum || stepNum === 1) && !assignment.isGlobal"
      :assignment="assignment"
      @advanceAssignmentEditor="advanceAssignmentEditor"
    />

    <!-- STEP 2: Criteria -->
    <assignment-edit-criteria
      v-else-if="stepNum === 2 && !assignment.isGlobal"
      :assignment="assignment"
      @advanceAssignmentEditor="advanceAssignmentEditor"
      @rewindAssignmentEditor="rewindAssignmentEditor"
    />

    <!-- STEP 3: Wordspotting -->
    <assignment-edit-wordspotting
      v-else-if="stepNum === 3 && !assignment.isGlobal"
      :assignment="assignment"
      @advanceAssignmentEditor="advanceAssignmentEditor"
      @rewindAssignmentEditor="rewindAssignmentEditor"
    />

    <!-- STEP 4: Ratings Descriptions -->
    <assignment-edit-ratings
      v-else-if="stepNum === 4 && !assignment.isGlobal"
      :assignment="assignment"
      @advanceAssignmentEditor="advanceAssignmentEditor"
      @rewindAssignmentEditor="rewindAssignmentEditor"
    />

    <!-- STEP 5: Attachments -->
    <assignment-edit-attachments
      v-else-if="stepNum === 5 && !assignment.isGlobal"
      :assignment="assignment"
      @assignmentModified="setCurrentAssignment"
      @advanceAssignmentEditor="advanceAssignmentEditor"
      @rewindAssignmentEditor="rewindAssignmentEditor"
    />

    <!-- STEP 6: Assignments Prompt -->
    <assignment-assignments-prompt
      v-else-if="stepNum === 6"
      :assignment="assignment"
      @advanceAssignmentEditor="advanceAssignmentEditor"
      @rewindAssignmentEditor="rewindAssignmentEditor"
    />

    <!-- STEP 7: Assignments -->
    <assignment-edit-assignments
      v-else-if="stepNum === 7"
      :assignment="assignment"
      @advanceAssignmentEditor="advanceAssignmentEditor"
      @rewindAssignmentEditor="rewindAssignmentEditor"
    />

    <div v-else>
      There was a problem with your request. Please return to your dashboard and try again.
    </div>
  </div>
</template>

<style lang="less" scoped>
</style>

<script>
import AssignmentEditCriteria from '../layout/AssignmentEditCriteria';
import AssignmentEditAttachments from '../layout/AssignmentEditAttachments';
import AssignmentEditAssignments from '../layout/AssignmentEditAssignments';
import AssignmentEditDescription from '../layout/AssignmentEditDescription';
import AssignmentAssignmentsPrompt from '../forms/AssignmentAssignmentsPrompt';
import AssignmentEditWordspotting from '../layout/AssignmentEditWordspotting';
import AssignmentEditRatings from '../layout/AssignmentEditRatings';

export default {
  components: {AssignmentEditDescription, AssignmentEditAssignments, AssignmentEditWordspotting,
    AssignmentEditRatings, AssignmentEditAttachments, AssignmentEditCriteria, AssignmentAssignmentsPrompt},
  data() {
    return {
      loading: true,
      assignment: {
        assignmentId: null,
        name: null,
        description: null,
        isShared: false,
        isTemplate: false,
        criteria: {},
      },
    };
  },
  computed: {
    assignments: {
      cache: false,
      get() {
        return this.$store.getters.assignments;
      }
    },
    assignmentStepsByName() {
      return this.$store.getters.appConfig.assignmentStepsByName;
    },
    steps() {
      return this.$store.getters.appConfig.assignmentSteps;
    },
    stepNum: {
      cache: false,
      get() {
        return parseInt(this.$router.currentRoute.params.stepNum, 10);
      }
    }
  },
  created() {
    const lookups = (this.$store.getters.assignments.length) ?
      [] : [
        this.$store.dispatch('listGroups'),
        this.$store.dispatch('listGroupMembers'),
        this.$store.dispatch('listAssignments'),
      ];

    window.Promise.all(lookups).then(() => {
      this.loading = false;
      this.setCurrentAssignment();
    });
  },
  methods: {
    setCurrentAssignment() {
      const curr =
        _.cloneDeep(_.find(
          this.assignments,
          s => s.assignmentId === this.$router.currentRoute.params.assignmentId
        ));
      if (curr && typeof curr.name !== 'undefined') {
        this.assignment = curr;
      } else {
        // Create a new assignment
        this.assignment = {
          name: '',
          description: '',
          isShared: false,
          criteria: {},
          attachments: [],
        };
      }
    },
    rewindAssignmentEditor() {
      let newStep = parseInt(this.stepNum, 10) - 1;

      do {
        const _newStep = this.steps[newStep - 1]; // convert stepNum to array index
        if (!_newStep || !_newStep.criteria || this.assignment.criteria[_newStep.criteria] ||
            (_newStep.criteria == 'scorecard' && this.assignment.scorecard)) {
          // If the step is not conditional, or it is conditional and it's ON, then use it
          break;
        }
        newStep--;
      } while (newStep > 1);

      this.$store.dispatch('listAssignments').then(() => {
        this.$router.push(
          { name: 'edit-assignment',
            params: {assignmentId: this.assignment.assignmentId, stepNum: newStep } }
        );
        this.setCurrentAssignment();
        this.$forceUpdate();
      });
    },
    advanceAssignmentEditor(newAssignmentId=null) {
      const maxStepNum = this.steps.length + 1;
      this.$store.dispatch('listAssignments').then(() => {
        if (this.stepNum === maxStepNum) {
          return this.$router.push({ name: 'coach-dashboard' });
        } else if (newAssignmentId !== null) {
          this.$router.push(
            { name: 'edit-assignment',
              params: {assignmentId: newAssignmentId, stepNum: this.assignmentStepsByName.criteria } }
          );
        } else {
          let newStep = parseInt(this.stepNum, 10) + 1;
          if (newStep >= maxStepNum) {
            return this.$router.push({name: 'coach-dashboard'});
          }

          do {
            const _newStep = this.steps[newStep - 1]; // convert stepNum to array index
            if (!_newStep || !_newStep.criteria || this.assignment.criteria[_newStep.criteria] ||
                (_newStep.criteria == 'scorecard' && this.assignment.scorecard)) {
              // If the step is not conditional, or it is conditional and it's ON, then use it
              break;
            }
            newStep++;
          } while (newStep < maxStepNum);

          this.$router.push(
            {
              name: 'edit-assignment',
              params: {assignmentId: this.assignment.assignmentId, stepNum: newStep}
            }
          );
        }
        this.setCurrentAssignment();
        this.$forceUpdate();
      });
    },
  }
};
</script>
